import type { UpdateProfileBody, UserRoleEnum } from "@nlc/shared-types";
import type { Control } from "react-hook-form";

import {
  isCustomerRole,
  isOwnerOrAdmin,
  isTeamRole,
  UserRoleEnum as Roles,
} from "@nlc/shared-types";
import { Controller } from "react-hook-form";

import type { UseAvatarUploadReturn } from "@/features/users/shared/hooks/useAvatarUpload";

import { Avatar } from "@/components/avatar";
import { Button } from "@/components/button";
import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import { Input, Label, TextField } from "@/components/field";
import { useResolveTimezones } from "@/features/users/hooks/useResolveTimezones";
import { AvatarCropModal } from "@/features/users/shared/components/AvatarCropModal";

import { ProfileCollaboratorFields } from "../ProfileCollaboratorFields";
import { ProfileOwnerAdminFields } from "../ProfileOwnerAdminFields";
import { ProfileTeamFields } from "../ProfileTeamFields";
import { TagListField } from "../TagListField";

interface ManageAccountTabProps {
  avatarUpload: UseAvatarUploadReturn;
  control: Control<UpdateProfileBody>;
  initials?: string;
  projects?: Array<{ id: number | string; name: string }>;
  roleDisplayName?: string;
  roleId?: UserRoleEnum;
  teamName?: string;
  userAvatar?: string;
  userId?: number | string;
  userName?: string;
}

export function ManageAccountTab({
  avatarUpload,
  control,
  initials,
  projects,
  roleDisplayName,
  roleId,
  teamName,
  userAvatar,
  userId,
  userName,
}: ManageAccountTabProps) {
  const isCustomer = isCustomerRole(roleId ?? 0);
  const isTeam = isTeamRole(roleId ?? 0);
  const isSuperAdmin = roleId === Roles.SuperAdmin;
  const showExtendedFields = isCustomer;
  const { data: timezones } = useResolveTimezones();

  const {
    cropImageSrc,
    fileInputRef,
    handleChangePhoto,
    handleCropApply,
    handleCropClose,
    handleFileSelect,
    handleRemove,
    isUploading: isAvatarUploading,
  } = avatarUpload;

  return (
    <>
      <div className="flex flex-col lg:flex-row items-start gap-6">
        <div className="flex flex-row lg:flex-col items-center gap-2">
          <Avatar
            alt={userName || "User"}
            className="size-20 rounded-full"
            initials={initials || ""}
            src={userAvatar || ""}
          />
          <Button
            isDisabled={isAvatarUploading}
            onPress={handleChangePhoto}
            size="sm"
            variant="secondary"
          >
            Change photo
          </Button>
          <Button
            color="destructive"
            isDisabled={isAvatarUploading || !userAvatar}
            onPress={handleRemove}
            size="sm"
            variant="tertiary"
          >
            Remove
          </Button>
          <input
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
            ref={fileInputRef}
            type="file"
          />
          {cropImageSrc && (
            <AvatarCropModal
              imageSrc={cropImageSrc}
              isOpen
              onApply={handleCropApply}
              onClose={handleCropClose}
            />
          )}
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {isTeam ? (
            <>
              <TextField>
                <Label>User role</Label>
                <Input disabled value={roleDisplayName || ""} />
              </TextField>

              <ProfileTeamFields
                control={control}
                isDisabled
                isSuperAdmin={isSuperAdmin}
                roleId={roleId}
                showDesignationOnly
              />

              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField isRequired>
                    <Label requiredHint>Name</Label>
                    <Input
                      {...field}
                      placeholder="Name"
                      value={field.value || ""}
                    />
                  </TextField>
                )}
              />

              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <TextField
                    className={isSuperAdmin ? "lg:col-span-2" : ""}
                    isRequired
                  >
                    <Label requiredHint>Email</Label>
                    <Input
                      {...field}
                      placeholder="mail@nlc.com"
                      type="email"
                      value={field.value || ""}
                    />
                  </TextField>
                )}
              />

              <ProfileTeamFields
                control={control}
                isDisabled
                isSuperAdmin={isSuperAdmin}
                roleId={roleId}
                showInitialsAndTeamOnly
                team={teamName || ""}
              />
            </>
          ) : (
            <>
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField isRequired>
                    <Label requiredHint>Name</Label>
                    <Input
                      {...field}
                      placeholder="Name"
                      value={field.value || ""}
                    />
                  </TextField>
                )}
              />

              <TextField>
                <Label>User role</Label>
                <Input disabled value={roleDisplayName || ""} />
              </TextField>

              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <TextField
                    className={showExtendedFields ? "" : "lg:col-span-2"}
                    isRequired
                  >
                    <Label requiredHint>Email</Label>
                    <Input
                      {...field}
                      disabled
                      placeholder="mail@nlc.com"
                      type="email"
                      value={field.value || ""}
                    />
                  </TextField>
                )}
              />
            </>
          )}

          {/* Extended fields for customer roles */}
          {showExtendedFields && (
            <>
              <Controller
                control={control}
                name="timezoneId"
                render={({ field }) => (
                  <TextField>
                    <Label>Timezone</Label>
                    <ComboBox
                      allowsEmptyCollection
                      menuTrigger="input"
                      onSelectionChange={field.onChange}
                      selectedKey={field.value}
                    >
                      <ComboBoxGroup>
                        <ComboBoxInput placeholder="Select a timezone" />
                        <ComboBoxButton />
                      </ComboBoxGroup>
                      <ComboBoxPopover>
                        <ComboBoxListBox className="max-h-64">
                          {timezones?.map((tz) => (
                            <ComboBoxListItem id={tz.id} key={tz.id}>
                              {tz.displayName}
                            </ComboBoxListItem>
                          ))}
                        </ComboBoxListBox>
                      </ComboBoxPopover>
                    </ComboBox>
                  </TextField>
                )}
              />

              <Controller
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <TextField className="lg:col-span-2">
                    <Input
                      {...field}
                      placeholder="000 00 00000000"
                      value={field.value || ""}
                    />
                  </TextField>
                )}
              />

              <TagListField
                className="lg:col-span-2"
                emptyMessage="No accounts assigned"
                items={projects}
                label="Accounts"
              />

              {roleId === Roles.Collaborator && userId && (
                <ProfileCollaboratorFields userId={userId} />
              )}

              {isOwnerOrAdmin(roleId ?? 0) && (
                <ProfileOwnerAdminFields
                  control={control}
                  isDisabledCustomerField
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-5">
        <Button type="submit">Save</Button>
      </div>
    </>
  );
}
